/** Roles that receive full TIPS Video Library access (matches bulk-upload / sidebar rules). */
function isElevatedPulseRole(role: any): boolean {
  if (!role) return false;
  if (role.is_super_admin || role.super_admin) return true;
  const name = String(role.name || role.role_name || '').trim().toLowerCase();
  return name === 'super admin' || name === 'superadmin' || name === 'administrator';
}

/** TIPS Video Library — module permissions derived from Directus role flags. */
export interface TipsVideoPermissionState {
  canViewLibrary: boolean;
  canViewDraftTab: boolean;
  canUseUnmappedFilter: boolean;
  canExport: boolean;
  canCreateEditVideo: boolean;
  canCreateInheritedVideo: boolean;
  canEditPublishedVideo: boolean;
  canEditAudio: boolean;
  audioReadOnly: boolean;
}

export function deriveTipsVideoPermissions(role: any): TipsVideoPermissionState {
  const r = role || {};
  const isSuper = isElevatedPulseRole(r);
  // Standalone create/edit — Video ISRC Maker (`tips_video_addedit`) or Super Admin only.
  const canCreateEditVideo = isSuper || !!r.tips_video_addedit;
  // From-audio create/edit — Video Maker OR Audio Maker (`tips_video_inherited_addedit`) or Super Admin.
  const canCreateInheritedVideo =
    isSuper || !!r.tips_video_addedit || !!r.tips_video_inherited_addedit;
  const canEditPublishedVideo = isSuper || !!r.edit_published_video_isrc_entry;
  const canEditAudio = isSuper || !!r.track_addedit;
  const canViewLibrary = true;
  const audioReadOnly = !canEditAudio && canViewLibrary;

  const canViewDraftTab = canCreateEditVideo || canCreateInheritedVideo;

  return {
    canViewLibrary,
    canViewDraftTab,
    canUseUnmappedFilter: canViewDraftTab,
    canExport: canViewLibrary,
    canCreateEditVideo,
    canCreateInheritedVideo,
    canEditPublishedVideo,
    canEditAudio,
    audioReadOnly,
  };
}

/** Whether the user may edit this Video ISRC record (draft rules unchanged; published uses its own flag). */
export function canEditVideoRecord(
  video: { status?: string; audio_track_id?: unknown } | null | undefined,
  permissions: TipsVideoPermissionState,
): boolean {
  if (!video) return false;
  if (String(video.status || '').toLowerCase() === 'published') {
    return permissions.canEditPublishedVideo;
  }
  return video.audio_track_id
    ? permissions.canCreateInheritedVideo
    : permissions.canCreateEditVideo;
}
